/* ============================================================
   sc-render-room.js
   The Render Room: the "Bespoke renders" view of the gallery page.

   Renders drawn for individual customers before their sofa was made,
   grouped into sets. A set is one conversation (one sofa, one fabric,
   the trims and legs that were tried), so the card shows the first
   render and the lightbox steps through the rest.

   Public surface
   --------------
   SC.renderRoom.sets              : the dataset (sc-render-room-data.js)
   SC.renderRoom.thumb(key, w)     : image key -> URL. Override to move
                                     the renders onto the SC CDN.
   SC.renderRoom.mount(rootId)     : build the toolbar + grid once
   SC.renderRoom.paint()           : re-filter and repaint
   SC.renderRoom.openSet(id, i)    : open the lightbox on a set
   SC.renderRoom.applyHash()       : honour a #set-id deep link

   Nothing here touches SC.gallery's data or favourites. The two views
   share the page and the chip styling, and nothing else.
   ============================================================ */
(function (global) {
  'use strict';
  var SC = global.SC = global.SC || {};
  SC.renderRoom = SC.renderRoom || {};
  var RR = SC.renderRoom;

  RR.sets = RR.sets || [];

  /* ---------------------- LIVE MANIFEST ---------------------- */
  /* The dataset bundled in the theme is the fallback and the first paint.
     The live set is a JSON file sitting in R2 beside the images, so adding
     a render or a whole set is an upload, not a theme push. Same shape as
     sc-render-room-data.js: { "sets": [ {id,t,m,f,imgs:[[file,caption]]} ] }.

     Mirrors SC.gallery.loadFromManifest: if the fetch fails, is
     unconfigured, or matches what is already on screen, the bundled data
     simply stays. */
  RR.loadFromManifest = function (url) {
    if (!url) return Promise.resolve(null);
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('render manifest ' + r.status);
      return r.json();
    }).then(function (data) {
      var sets = (data && data.sets) || [];
      if (!sets.length) return null;
      RR.sets = sets;
      return sets;
    }).catch(function (e) {
      console.warn('Render Room manifest unavailable, using bundled data.', e);
      return null;
    });
  };

  /* Cheap identity for a set list, so a manifest identical to the bundled
     data does not trigger a visible rebuild. */
  RR.signature = function (sets) {
    return (sets || []).map(function (s) {
      return s.id + ':' + s.imgs.length;
    }).join('|');
  };

  /* ---------------------- IMAGE URLS ---------------------- */
  /* One template, two tokens: {id} is the key stored in the dataset (the
     render's filename), {w} the pixel width we want. Sized through
     Cloudflare image resizing, the same way the gallery serves its pins,
     because the originals are 1.5-6MB PNGs and a grid of 54 of them at
     full size would be unusable.

     Bespoke renders sit in renders/bespoke/, deliberately apart from the
     approved renders in renders/: four of these filenames match an
     approved render exactly, and fifteen share its SKU+view key, so a
     shared folder would either overwrite an approved render or shadow it
     in SC.renderManifest.

     A key that is already an absolute URL is passed straight through, so
     a one-off can be pointed anywhere without touching the dataset. */
  RR.thumbBase = RR.thumbBase ||
    'https://cdn.sophieconran.com/cdn-cgi/image/width={w},quality=82,format=auto/renders/bespoke/{id}';

  RR.thumb = function (key, w) {
    if (!key) return '';
    if (/^https?:\/\//i.test(key)) return key;
    return RR.thumbBase
      .replace('{w}', String(w || 800))
      .replace('{id}', encodeURIComponent(key));
  };

  var GRID_W = 800;
  var LIGHTBOX_W = 1600;

  /* ---------------------- STATE ---------------------- */
  RR.state = { model: 'all', feats: [], q: '' };

  /* Preferred chip order. Anything in the data but not listed is
     appended rather than dropped, so a new tag on a new set still gets
     a chip without an edit here. */
  var MODEL_ORDER = ['Coco', 'Felix', 'Isobel', 'Barton', 'Henrietta', 'Sophia'];
  var FEATURE_ORDER = [
    'Sofa Beds', 'Corner & Modular', 'Chaise', 'Loveseats', 'Armchairs', 'Footstools',
    'Coffee Tables', 'Florals', 'Stripes', 'Skirts', 'Contrast Trim', 'Velvet Trim',
    'Box Edge', 'Knife Edge', 'Legs'
  ];

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function slug(s) {
    return String(s).toLowerCase().replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /* Ordered, de-duplicated list of every value present in the data for
     one key ('m' for ranges, 'f' for features). */
  function valuesFor(key, order) {
    var seen = {}, present = [];
    RR.sets.forEach(function (s) {
      (s[key] || []).forEach(function (v) {
        if (!seen[v]) { seen[v] = true; present.push(v); }
      });
    });
    var known = order.filter(function (v) { return seen[v]; });
    var extra = present.filter(function (v) { return order.indexOf(v) === -1; }).sort();
    return known.concat(extra);
  }

  RR.models = function () { return valuesFor('m', MODEL_ORDER); };
  RR.features = function () { return valuesFor('f', FEATURE_ORDER); };

  /* ---------------------- FILTERING ---------------------- */
  function matches(s) {
    var st = RR.state;
    if (st.model !== 'all' && s.m.indexOf(st.model) === -1) return false;
    for (var i = 0; i < st.feats.length; i++) {
      if (s.f.indexOf(st.feats[i]) === -1) return false;
    }
    if (st.q) {
      var hay = (s.t + ' ' + s.m.join(' ') + ' ' + s.f.join(' ') + ' ' +
                 s.imgs.map(function (im) { return im[1]; }).join(' ')).toLowerCase();
      if (hay.indexOf(st.q) === -1) return false;
    }
    return true;
  }

  RR.filtered = function () { return RR.sets.filter(matches); };

  /* ---------------------- MARKUP ---------------------- */
  function chipMarkup(attr, value, label, active) {
    return '<button type="button" class="sc-filter-chip sc-rr-chip' +
      (active ? ' is-active' : '') + '" ' + attr + '="' + esc(value) + '"' +
      ' aria-pressed="' + (active ? 'true' : 'false') + '">' + esc(label) + '</button>';
  }

  function cardMarkup(s) {
    var n = s.imgs.length;
    return '' +
      '<article class="sc-rr-card" id="' + esc(s.id) + '" data-sc-rr-set="' + esc(s.id) + '">' +
        '<button type="button" class="sc-rr-thumb" data-sc-rr-open="' + esc(s.id) + '"' +
          ' aria-label="View ' + n + ' render' + (n > 1 ? 's' : '') + ': ' + esc(s.t) + '">' +
          '<img loading="lazy" decoding="async" src="' + esc(RR.thumb(s.imgs[0][0], GRID_W)) + '"' +
            ' alt="' + esc(s.imgs[0][1]) + '">' +
          '<span class="sc-rr-count-badge">' + n + ' render' + (n > 1 ? 's' : '') + '</span>' +
        '</button>' +
        '<div class="sc-rr-body">' +
          '<h3 class="sc-rr-title">' + esc(s.t) + '</h3>' +
          '<div class="sc-rr-tags">' +
            s.m.concat(s.f).map(function (t) {
              return '<span class="sc-rr-tag">' + esc(t) + '</span>';
            }).join('') +
          '</div>' +
          '<div class="sc-rr-actions">' +
            '<button type="button" class="sc-rr-btn sc-rr-btn-primary" data-sc-rr-open="' + esc(s.id) + '">View renders</button>' +
            '<button type="button" class="sc-rr-btn sc-rr-btn-ghost" data-sc-rr-copy="' + esc(s.id) + '">Copy link</button>' +
          '</div>' +
        '</div>' +
      '</article>';
  }

  /* ---------------------- MOUNT ---------------------- */
  var root = null;

  RR.mount = function (rootId) {
    root = document.getElementById(rootId);
    if (!root) return;
    /* Skeleton is built once. paint() only swaps the grid and toggles
       chip state, so the search box never loses focus mid-typing. */
    if (!root.getAttribute('data-sc-rr-ready')) {
      root.innerHTML =
        '<div class="sc-rr-bar">' +
          '<div class="sc-rr-bar-inner">' +
            '<div class="sc-rr-search">' +
              '<input type="search" data-sc-rr-q placeholder="Search a fabric, a colour, a sofa" aria-label="Search the renders">' +
            '</div>' +
            '<div class="sc-rr-chips" data-sc-rr-models role="group" aria-label="Filter renders by range"></div>' +
            '<div class="sc-rr-chips sc-rr-chips-feat" data-sc-rr-feats role="group" aria-label="Filter renders by detail"></div>' +
            '<p class="sc-rr-count" data-sc-rr-count aria-live="polite"></p>' +
          '</div>' +
        '</div>' +
        '<div class="sc-rr-grid" data-sc-rr-grid></div>' +
        '<div class="sc-rr-empty" data-sc-rr-empty hidden>' +
          '<span>Nothing in that corner yet</span>' +
          'Try a different fabric, or clear a filter.' +
        '</div>';

      root.querySelector('[data-sc-rr-models]').innerHTML =
        [chipMarkup('data-sc-rr-model', 'all', 'All ranges', true)].concat(
          RR.models().map(function (m) { return chipMarkup('data-sc-rr-model', m, m, false); })
        ).join('');

      root.querySelector('[data-sc-rr-feats]').innerHTML =
        RR.features().map(function (f) {
          return chipMarkup('data-sc-rr-feat', f, f, false);
        }).join('');

      bind();
      root.setAttribute('data-sc-rr-ready', '1');
    }
    RR.paint();
  };

  /* Rebuild from scratch after the dataset is swapped: the chips are built
     once at mount, so a new set list needs the skeleton discarded, not just
     a repaint. Filters are reset because a chip may no longer exist. */
  RR.reset = function () {
    if (!root) return;
    root.removeAttribute('data-sc-rr-ready');
    RR.state = { model: 'all', feats: [], q: '' };
    RR.mount(root.id);
  };

  RR.paint = function () {
    if (!root) return;
    var grid = root.querySelector('[data-sc-rr-grid]');
    var empty = root.querySelector('[data-sc-rr-empty]');
    var count = root.querySelector('[data-sc-rr-count]');
    var shown = RR.filtered();

    grid.innerHTML = shown.map(cardMarkup).join('');
    if (empty) empty.hidden = shown.length > 0;

    if (count) {
      var total = RR.sets.reduce(function (n, s) { return n + s.imgs.length; }, 0);
      count.textContent = shown.length === RR.sets.length
        ? RR.sets.length + ' render sets · ' + total + ' renders in the room'
        : shown.length + ' of ' + RR.sets.length + ' render sets';
    }

    /* Reflect state on the chips. */
    root.querySelectorAll('[data-sc-rr-model]').forEach(function (b) {
      var on = b.getAttribute('data-sc-rr-model') === RR.state.model;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    root.querySelectorAll('[data-sc-rr-feat]').forEach(function (b) {
      var on = RR.state.feats.indexOf(b.getAttribute('data-sc-rr-feat')) !== -1;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  /* ---------------------- BINDINGS ---------------------- */
  function bind() {
    root.addEventListener('click', function (e) {
      var el = e.target.closest('[data-sc-rr-model],[data-sc-rr-feat],[data-sc-rr-open],[data-sc-rr-copy]');
      if (!el || !root.contains(el)) return;

      var model = el.getAttribute('data-sc-rr-model');
      if (model) { RR.state.model = model; RR.paint(); return; }

      var feat = el.getAttribute('data-sc-rr-feat');
      if (feat) {
        var i = RR.state.feats.indexOf(feat);
        if (i === -1) RR.state.feats.push(feat); else RR.state.feats.splice(i, 1);
        RR.paint();
        return;
      }

      var open = el.getAttribute('data-sc-rr-open');
      if (open) { RR.openSet(open, 0); return; }

      var copy = el.getAttribute('data-sc-rr-copy');
      if (copy) { copyLink(copy); }
    });

    var q = root.querySelector('[data-sc-rr-q]');
    if (q) {
      q.addEventListener('input', function () {
        RR.state.q = q.value.trim().toLowerCase();
        RR.paint();
      });
    }

    /* An image that will not load leaves a broken icon in the middle of
       a card. Mark the card instead: the caption and tags still say what
       the set is, and the set still opens. Image errors do not bubble,
       hence the capture phase. */
    root.addEventListener('error', function (e) {
      var img = e.target;
      if (!img || img.tagName !== 'IMG') return;
      var card = img.closest('.sc-rr-card');
      if (card) card.classList.add('is-imageless');
    }, true);
  }

  function copyLink(id) {
    var url;
    try {
      var u = new URL(global.location.href);
      /* "room", not "view" — Shopify reserves ?view= for alternate
         templates and would serve the page's default template instead. */
      u.searchParams.delete('view');
      u.searchParams.set('room', 'bespoke-renders');
      u.hash = id;
      url = u.toString();
    } catch (e) {
      url = global.location.href.split('#')[0] + '#' + id;
    }
    var done = function () { RR.toast('Link copied, ready to share'); };
    if (global.navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done, function () { RR.toast(url); });
    } else {
      RR.toast(url);
    }
  }

  /* ---------------------- TOAST ---------------------- */
  var toastEl = null, toastTimer = null;
  RR.toast = function (msg) {
    if (SC.account && SC.account.toast) { SC.account.toast(msg); return; }
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'sc-rr-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-open');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-open'); }, 2400);
  };

  /* ---------------------- LIGHTBOX ---------------------- */
  var lb = null, currentSet = null, currentIdx = 0, lastFocus = null;

  function buildLightbox() {
    lb = document.createElement('div');
    lb.className = 'sc-rr-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Render gallery');
    lb.hidden = true;
    lb.innerHTML =
      '<button type="button" class="sc-rr-lb-close" data-sc-rr-close aria-label="Close gallery">&times;</button>' +
      '<div class="sc-rr-lb-stage">' +
        '<button type="button" class="sc-rr-lb-nav sc-rr-lb-prev" data-sc-rr-step="-1" aria-label="Previous render">&#8249;</button>' +
        '<img data-sc-rr-lb-img alt="">' +
        '<button type="button" class="sc-rr-lb-nav sc-rr-lb-next" data-sc-rr-step="1" aria-label="Next render">&#8250;</button>' +
      '</div>' +
      '<div class="sc-rr-lb-cap">' +
        '<span data-sc-rr-lb-cap></span>' +
        '<span class="sc-rr-lb-num" data-sc-rr-lb-num></span>' +
      '</div>';
    document.body.appendChild(lb);

    lb.addEventListener('click', function (e) {
      if (e.target === lb) { RR.closeSet(); return; }
      var step = e.target.closest('[data-sc-rr-step]');
      if (step) { e.stopPropagation(); RR.step(parseInt(step.getAttribute('data-sc-rr-step'), 10)); return; }
      if (e.target.closest('[data-sc-rr-close]')) RR.closeSet();
    });

    document.addEventListener('keydown', function (e) {
      if (!lb || lb.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); RR.closeSet(); }
      else if (e.key === 'ArrowLeft') RR.step(-1);
      else if (e.key === 'ArrowRight') RR.step(1);
    });
  }

  function setById(id) {
    for (var i = 0; i < RR.sets.length; i++) {
      if (RR.sets[i].id === id) return RR.sets[i];
    }
    return null;
  }

  function showImage() {
    if (!currentSet) return;
    var pair = currentSet.imgs[currentIdx];
    var img = lb.querySelector('[data-sc-rr-lb-img]');
    img.src = RR.thumb(pair[0], LIGHTBOX_W);
    img.alt = pair[1];
    lb.querySelector('[data-sc-rr-lb-cap]').textContent = pair[1];
    lb.querySelector('[data-sc-rr-lb-num]').textContent =
      currentSet.t + ' · ' + (currentIdx + 1) + ' of ' + currentSet.imgs.length;
    var many = currentSet.imgs.length > 1;
    lb.querySelectorAll('.sc-rr-lb-nav').forEach(function (b) { b.hidden = !many; });
  }

  RR.openSet = function (id, index) {
    var s = typeof id === 'string' ? setById(id) : id;
    if (!s) return;
    if (!lb) buildLightbox();
    lastFocus = document.activeElement;
    currentSet = s;
    currentIdx = index || 0;
    showImage();
    lb.hidden = false;
    document.body.classList.add('sc-rr-lb-open');
    lb.querySelector('[data-sc-rr-close]').focus();
  };

  RR.closeSet = function () {
    if (!lb || lb.hidden) return;
    lb.hidden = true;
    currentSet = null;
    document.body.classList.remove('sc-rr-lb-open');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  };

  RR.step = function (d) {
    if (!currentSet) return;
    var n = currentSet.imgs.length;
    currentIdx = (currentIdx + d + n) % n;
    showImage();
  };

  /* ---------------------- DEEP LINKS ---------------------- */
  /* #set-id scrolls to a card and flashes it, the way the standalone
     Render Room page did, so shared links still land where they should.
     Returns true when a card was found. */
  RR.applyHash = function () {
    if (!root || !location.hash || location.hash.length < 2) return false;
    var id = decodeURIComponent(location.hash.slice(1));
    var card = root.querySelector('[data-sc-rr-set="' + (global.CSS && CSS.escape ? CSS.escape(id) : id) + '"]');
    if (!card) return false;
    setTimeout(function () {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      card.classList.add('is-flash');
      setTimeout(function () { card.classList.remove('is-flash'); }, 2600);
    }, 200);
    return true;
  };

  RR.hasSet = function (id) { return !!setById(id); };

}(typeof window !== 'undefined' ? window : this));
